'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { formatLocalDate } from '@/lib/date-utils';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SellerStats } from '@kit/marketplace/services/review';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Avatar, AvatarFallback } from '@kit/ui/avatar';
import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@kit/ui/sheet';
import { Trans } from '@kit/ui/trans';

import { RatingBadge } from './rating-badge';
import { ReviewFormDialog } from './review-form-dialog';
import { Stars } from './stars';

interface ReviewItem {
  id: string;
  rating: number;
  as_described: number | null;
  tags: string[];
  body: string | null;
  is_verified_purchase: boolean;
  created_at: string;
  reviewer: { first_name: string | null } | null;
}

interface SellerReviewsLauncherProps {
  seller: {
    id: string;
    name: string;
    verified?: boolean;
    city?: string | null;
  };
  stats: SellerStats;
  /** Whether the current viewer may write a review (eligibility computed server-side). */
  canReview: boolean;
  listingId?: string;
  /** Path to revalidate after posting. */
  revalidatePath?: string;
  /** Badge size + class passthrough so cards and detail can tune it. */
  badgeClassName?: string;
}

function initialOf(name: string) {
  return name.trim().charAt(0).toUpperCase() || '?';
}

function formatDate(iso: string) {
  // created_at is a full ISO timestamp; format the date part timezone-safely
  // (Critical Rule #19 — no `new Date(string).toLocaleDateString()`).
  return formatLocalDate(iso.slice(0, 10), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function aggregateTags(reviews: ReviewItem[]): Array<[string, number]> {
  const counts: Record<string, number> = {};
  for (const r of reviews) {
    for (const t of r.tags ?? []) counts[t] = (counts[t] ?? 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1]);
}

/**
 * Single entry point for seller reputation: a rating badge that opens a
 * right-side reviews drawer (stats + the seller's reviews) with a
 * write-review action gated on `canReview`. The review list is lazy-loaded
 * the first time the drawer opens (public RLS read), so browse cards don't
 * pay for it up front.
 */
export function SellerReviewsLauncher({
  seller,
  stats,
  canReview,
  listingId,
  revalidatePath,
  badgeClassName,
}: SellerReviewsLauncherProps) {
  const { t } = useTranslation();
  const supabase = useSupabase();
  const router = useRouter();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [reviews, setReviews] = useState<ReviewItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    // eslint-disable-next-line supabase-query/no-unbounded-select -- reviews of one marketplace seller
    const { data, error } = await supabase
      .from('marketplace_seller_reviews')
      .select(
        'id, rating, as_described, tags, body, is_verified_purchase, created_at, reviewer:profiles!marketplace_seller_reviews_reviewer_id_fkey(first_name)',
      )
      .eq('seller_id', seller.id)
      .order('created_at', { ascending: false });
    // Distinguish a failed load (RLS / network) from a seller with zero
    // reviews — keep `reviews` null on error so we render a retry, not an
    // indistinguishable empty list.
    if (error) {
      setLoadError(true);
    } else {
      setReviews((data ?? []) as unknown as ReviewItem[]);
    }
    setLoading(false);
  }, [supabase, seller.id]);

  // Lazy-load the review list on first open — driven by the open event, not an
  // effect (react-hooks/set-state-in-effect). Browse cards don't pay for it
  // until the drawer is actually opened.
  const openSheet = useCallback(() => {
    setSheetOpen(true);
    if (reviews === null && !loading) void load();
  }, [reviews, loading, load]);

  const tags = reviews ? aggregateTags(reviews) : [];

  return (
    <>
      <RatingBadge
        stats={stats}
        onClick={openSheet}
        className={badgeClassName}
      />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        >
          <SheetHeader className="border-b p-5">
            <div className="flex items-center gap-3">
              <Avatar className="size-12">
                <AvatarFallback>{initialOf(seller.name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <SheetTitle className="flex items-center gap-2 text-lg">
                  {seller.name}
                  {seller.verified && (
                    <span className="text-primary inline-flex items-center gap-1 text-xs font-medium">
                      <ShieldCheck className="size-3.5" />{' '}
                      <Trans i18nKey="explore:reviews.verified" />
                    </span>
                  )}
                </SheetTitle>
                {seller.city && (
                  <p className="text-muted-foreground text-sm">{seller.city}</p>
                )}
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            {stats.reviewCount > 0 ? (
              <div className="bg-muted/40 space-y-3 rounded-lg border p-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl leading-none font-bold">
                    {stats.avgRating.toFixed(1)}
                  </span>
                  <Stars value={stats.avgRating} size={18} />
                  <span className="text-muted-foreground text-sm">
                    {t('explore:reviews.reviewCount', {
                      count: stats.reviewCount,
                    })}
                  </span>
                </div>
                {stats.avgAsDescribed > 0 && (
                  <div className="text-muted-foreground flex items-center gap-2 border-t pt-3 text-sm">
                    <span>
                      <Trans i18nKey="explore:reviews.itemAsDescribed" />
                    </span>
                    <Stars value={stats.avgAsDescribed} size={14} />
                    <span className="text-foreground font-semibold">
                      {stats.avgAsDescribed.toFixed(1)}
                    </span>
                  </div>
                )}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map(([tag, count]) => (
                      <Badge key={tag} variant="secondary">
                        {tag} {count}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                {t('explore:reviews.empty', {
                  name: seller.name.split(' ')[0],
                })}
              </p>
            )}

            {canReview ? (
              <Button className="w-full" onClick={() => setFormOpen(true)}>
                <Trans i18nKey="explore:reviews.writeReview" />
              </Button>
            ) : (
              <p className="text-muted-foreground inline-flex items-center gap-1.5 text-xs">
                <ShieldCheck className="text-primary size-3.5" />
                <Trans i18nKey="explore:reviews.eligibilityNote" />
              </p>
            )}

            <div className="space-y-4">
              {loading && reviews === null && (
                <p className="text-muted-foreground text-sm">
                  <Trans i18nKey="explore:reviews.loading" />
                </p>
              )}
              {loadError && !loading && (
                <div className="space-y-2 border-t pt-4">
                  <p className="text-muted-foreground text-sm">
                    <Trans i18nKey="explore:reviews.loadError" />
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void load()}
                  >
                    <Trans i18nKey="explore:reviews.retry" />
                  </Button>
                </div>
              )}
              {(reviews ?? []).map((r) => (
                <article key={r.id} className="space-y-2 border-t pt-4">
                  <div className="flex items-center gap-2">
                    <Avatar className="size-7">
                      <AvatarFallback className="text-xs">
                        {initialOf(
                          r.reviewer?.first_name ??
                            t('explore:reviews.buyerFallback'),
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 leading-tight">
                      <p className="text-sm font-medium">
                        {r.reviewer?.first_name ??
                          t('explore:reviews.buyerFallback')}
                      </p>
                      {r.is_verified_purchase && (
                        <p className="text-muted-foreground inline-flex items-center gap-1 text-xs">
                          <ShieldCheck className="text-primary size-3" />
                          <Trans i18nKey="explore:reviews.verifiedPurchase" />
                        </p>
                      )}
                    </div>
                    <span className="text-muted-foreground ml-auto text-xs">
                      {formatDate(r.created_at)}
                    </span>
                  </div>
                  <Stars value={r.rating} size={15} />
                  {r.body && (
                    <p className="text-sm leading-relaxed">{r.body}</p>
                  )}
                  {r.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {r.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <ReviewFormDialog
        key={formOpen ? 'open' : 'closed'}
        open={formOpen}
        onOpenChange={setFormOpen}
        sellerId={seller.id}
        sellerName={seller.name}
        listingId={listingId}
        revalidatePath={revalidatePath}
        onSubmitted={() => {
          setReviews(null); // force a reload of the list
          router.refresh(); // refresh server stats on the host page
        }}
      />
    </>
  );
}
